import { useMutation, useQuery, useQueryClient } from "react-query"
import { getMerchantStatus, setMerchantApplicationStatus, simulateNewChargeback, simulateNewAchReturn, simulateChargebackStatusChange, simulateNewDeposit, startMerchantApplication, simulateDepositStatusChange, getMerchantOnboardingSession, getReportingSession, updateNextDayAch } from "./rainforestApi"
import { notification } from "antd"
import { AxiosError } from "axios"

export const useRainforestMerchantStatusQuery = (enabled = true) => {
  return useQuery(['rainforest', 'merchant-status'], {
    queryFn: () => getMerchantStatus(),
    refetchOnWindowFocus: false,
    enabled,
  })
}

export const useUpdateNextDayAchMutation = (onSuccess: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateNextDayAch,
    onSuccess: () => {
      queryClient.invalidateQueries(['rainforest', 'merchant-status'])
      onSuccess()
    },
    onError: (error: AxiosError) => {
      const message = (error.response?.data as any)?.error ?? 'There was an error updating Next-Day ACH Payments. Please try again later.'
      notification.error({ message })
    }
  })
}

export const useStartMerchantApplicationMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (useSandbox: boolean) => startMerchantApplication(useSandbox),
    onSuccess: () => {
      queryClient.setQueryData(['rainforest', 'merchant-status'], {
        applicationId: '',
        applicationStatus: 'CREATED',
        merchantId: '',
        merchantStatus: 'PENDING'
      })
      queryClient.invalidateQueries(['rainforest', 'merchant-status'])
    },
    onError: (error: AxiosError) => {
      const message = (error.response?.data as any)?.errorMessage ?? 'There was an error starting your application. Please try again in a few minutes.'
      notification.error({ message })
    }
  })
}

export const useSetMerchantApplicationStatusMutation = (onFinished: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: setMerchantApplicationStatus,
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries(['rainforest', 'merchant-status'])
        onFinished()
      }, 5000)
    },
    onError: (error: AxiosError) => {
      const message = (error.response?.data as any)?.errorMessage ?? 'An error occurred while setting merchant application status.'
      notification.error({ message })
      onFinished()
    }
  })
}

export const useRainforestMerchantOnboardingSessionQuery = () => {
  return useQuery(['rainforest', 'merchant-onboarding'], {
    queryFn: () => getMerchantOnboardingSession(),
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'Error loading access session.' })
    }
  })
}

export const useRainforestReportingSessionQuery = () => {
  return useQuery(['rainforest', 'reporting'], {
    queryFn: () => getReportingSession(),
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'Error loading access session.' })
    }
  })
}

export const useSimulateNewChargebackMutation = () => {
  return useMutation({
    mutationFn: simulateNewChargeback,
    onError: (error: AxiosError) => {
      const message = (error.response?.data as any)?.errorMessage ?? 'There was an error simulating a new chargeback. Please check the payin ID before trying again.'
      notification.error({ message })
    }
  })
}

export const useSimulateChargebackStatusChangeMutation = () => {
  return useMutation({
    mutationFn: simulateChargebackStatusChange,
    onError: (error: AxiosError) => {
      const message = (error.response?.data as any)?.errorMessage ?? 'There was an error simulating a chargeback status change. Please check the chargeback ID and status change before trying again.'
      notification.error({ message })
    }
  })
}

export const useSimulateNewAchReturnMutation = () => {
  return useMutation({
    mutationFn: simulateNewAchReturn,
    onError: (error: AxiosError) => {
      const message = (error.response?.data as any)?.errorMessage ?? 'There was an error simulating an ACH return. Please check the payin ID before trying again.'
      notification.error({ message })
    }
  })
}

export const useSimulateNewDepositMutation = (onSuccess: () => void) => {
  return useMutation({
    mutationFn: simulateNewDeposit,
    onSuccess,
    onError: (error: AxiosError) => {
      const message = (error.response?.data as any)?.errorMessage ?? 'There was an error simulating a new deposit.'
      notification.error({ message })
    }
  })
}

export const useSimulateDepositStatusChangeMutation = () => {
  return useMutation({
    mutationFn: simulateDepositStatusChange,
    onError: (error: AxiosError) => {
      const message = (error.response?.data as any)?.errorMessage ?? 'There was an error simulating a deposit status change. Please check the deposit ID before trying again.'
      notification.error({ message })
    }
  })
}
